import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@app/core';
import { ZipDownloadService } from '@app/modules/invoice/services';
import { User } from '@sentry/angular-ivy';

import { PdfJsViewerComponent } from "ng2-pdfjs-viewer";

@Component({
  selector: 'app-pdf-zoom-review',
  templateUrl: './pdf-zoom-review.component.html',
  styleUrls: ['./pdf-zoom-review.component.scss']
})
export class PdfZoomReviewComponent implements OnChanges, OnDestroy {
  @Input() name?: string;
  @Input() path: string;
  @Input() page?: number;
  @Input() sources: any;
  @Input() isFullScreen: boolean;
  @Input() isDuplicate: boolean;
  @Input() showCloseIcon: boolean;
  @Input() showFullScreenBtn: boolean;
  @Input() zoomScale: string = "'page-width'";
  @Output() closeEvent: EventEmitter<{ isCloseClicked: boolean }> = new EventEmitter(null);
  @Output() fullScreenEvent: EventEmitter<{ isFullScreen: boolean }> = new EventEmitter(false);
  @ViewChild("pdfViewer") pdfViewer!: PdfJsViewerComponent;

  isDragging = false;
  startX: number;
  startY: number;
  initialPosX: number = 0;
  initialPosY: number = 0;
  posX: number = 0;
  posY: number = 0;
  zoom: number = 1;
  minZoom: number = 1;
  maxZoom: number = 3;
  zoomIncrement: number = 0.4;
  rotation: number = 0;
  downlaodVisible: boolean = false;
  loggedinUser: User;
  // viewerSrc is the actual source passed to the viewer. We prefetch the PDF
  // into a Blob URL and set viewerSrc to that (plain string) so the viewer
  // receives a normal URL string. Passing a DomSanitizer SafeValue into the
  // 3rd-party viewer caused pdfjs to attempt to fetch a path like
  // "/assets/pdfjs/web/SafeValue%20must%20use%20[property]".
  viewerSrc: string | null = null;
  private _currentObjectUrl: string | null = null;

  constructor(
    public zipDownloadService: ZipDownloadService,
    public router: Router,
    private readonly authService: AuthService) {

    this.downlaodVisible = (/^\/invoice\/[a-f\d]{24}$/i.test(this.router.url) || /^\/invoice\/[a-f0-9]{24}\/image-only$/i.test(this.router.url)) && this.authService.user.isAdmin;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.isFullScreen?.currentValue) {
      document.body.style.overflow = 'hidden';
    } else if (changes) {
      document.body.style.overflow = '';
      this.reset();
    }

    // If `path` changed, try prefetching the PDF first so the viewer doesn't
    // simultaneously start its own request (which previously caused truncated
    // / malformed presigned URL requests). We intentionally handle this
    // asynchronously so change detection isn't blocked.
    if (changes?.path?.currentValue) {
      const rawPath = changes.path.currentValue as string;
      const normalized = rawPath;
      this.viewerSrc = null; // hide viewer while we attempt prefetch
      this.handlePathChange(normalized);
    }
  }

  private async handlePathChange(url: string) {
    const ok = await this.prefetchPdfToBlobUrl(url);
    if (ok && this._currentObjectUrl) {
      // Pass the plain object URL string to the viewer.
      this.viewerSrc = this._currentObjectUrl;
    } else {
      // prefetch failed: fall back to the original URL so viewer can try
      // loading it directly (this will surface the exact S3 error).
      this.viewerSrc = url;
    }
  }

  /**
   * Prefetch the PDF and create a Blob URL stored in `_currentObjectUrl`.
   * Returns true when successful.
   */
  private async prefetchPdfToBlobUrl(url: string): Promise<boolean> {
    this.revokeCurrentObjectUrl();
    if (!url) return false;
    try {
      const res = await fetch(url, { method: 'GET' });
      if (!res.ok) {
        const body = await res.text().catch(() => null);
        console.error('prefetchPdfToBlobUrl failed fetching PDF:', res.status, body);
        return false;
      }
      const ab = await res.arrayBuffer();
      const blob = new Blob([ab], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(blob);
      this._currentObjectUrl = objectUrl;
      return true;
    } catch (err) {
      console.error('prefetchPdfToBlobUrl unexpected error', err);
      return false;
    }
  }

  private revokeCurrentObjectUrl() {
    if (this._currentObjectUrl) {
      URL.revokeObjectURL(this._currentObjectUrl);
      this._currentObjectUrl = null;
    }
  }

  ngOnDestroy(): void {
    this.revokeCurrentObjectUrl();
  }


  downloadFilesAsZip() {
    this.zipDownloadService.downloadFilesAsZip(this.sources);
  }

  zoomIn() {
    if (this.zoom < this.maxZoom) {
      this.zoom += this.zoomIncrement;
    }
    // Try to apply zoom to the embedded viewer (PDF.js) when available.
    try {
      const app = (this.pdfViewer as any)?.PDFViewerApplication;
      // If viewer exposes a zoomIn helper, prefer that.
      if (app && typeof app.zoomIn === 'function') {
        app.zoomIn();
        return;
      }
      const pdfViewer = app?.pdfViewer;
      if (pdfViewer) {
        const current = typeof pdfViewer.currentScale === 'number' ? pdfViewer.currentScale : parseFloat(pdfViewer.currentScaleValue) || 1;
        const newScale = Math.min(current + this.zoomIncrement, this.maxZoom);
        try {
          if (typeof pdfViewer.currentScale === 'number') {
            pdfViewer.currentScale = newScale;
          }
        } catch (e) { /* ignore */ }
        try {
          if (pdfViewer.currentScaleValue !== undefined) pdfViewer.currentScaleValue = String(newScale);
        } catch (e) { /* ignore */ }
        // notify event bus if present
        if (app?.eventBus && typeof app.eventBus.dispatch === 'function') {
          try { app.eventBus.dispatch('scalechanging', { scale: newScale }); } catch (e) { /* ignore */ }
        }
        if (typeof pdfViewer._resetView === 'function') try { pdfViewer._resetView(); } catch (e) { /* ignore */ }
        if (typeof (this.pdfViewer as any).refresh === 'function') try { (this.pdfViewer as any).refresh(); } catch (e) { /* ignore */ }
        return;
      }
    } catch (err) {
      console.warn('zoomIn: viewer API failed, falling back to component input', err);
    }

    // Fallback: set the component input `zoom` on the wrapper so the library can pick it up.
    try {
      if (this.pdfViewer) {
        (this.pdfViewer as any).zoom = String(this.zoom);
        if (typeof (this.pdfViewer as any).refresh === 'function') (this.pdfViewer as any).refresh();
      }
    } catch (e) { /* ignore */ }
  }

  zoomOut() {
    if (this.zoom > this.minZoom) {
      this.zoom -= this.zoomIncrement;
    }
    // Try to apply zoom out to the embedded viewer (PDF.js) when available.
    try {
      const app = (this.pdfViewer as any)?.PDFViewerApplication;
      if (app && typeof app.zoomOut === 'function') {
        app.zoomOut();
        return;
      }
      const pdfViewer = app?.pdfViewer;
      if (pdfViewer) {
        const current = typeof pdfViewer.currentScale === 'number' ? pdfViewer.currentScale : parseFloat(pdfViewer.currentScaleValue) || 1;
        const newScale = Math.max(current - this.zoomIncrement, this.minZoom);
        try {
          if (typeof pdfViewer.currentScale === 'number') pdfViewer.currentScale = newScale;
        } catch (e) { /* ignore */ }
        try { if (pdfViewer.currentScaleValue !== undefined) pdfViewer.currentScaleValue = String(newScale); } catch (e) { /* ignore */ }
        if (app?.eventBus && typeof app.eventBus.dispatch === 'function') {
          try { app.eventBus.dispatch('scalechanging', { scale: newScale }); } catch (e) { /* ignore */ }
        }
        if (typeof pdfViewer._resetView === 'function') try { pdfViewer._resetView(); } catch (e) { /* ignore */ }
        if (typeof (this.pdfViewer as any).refresh === 'function') try { (this.pdfViewer as any).refresh(); } catch (e) { /* ignore */ }
        return;
      }
    } catch (err) {
      console.warn('zoomOut: viewer API failed, falling back to component input', err);
    }

    // Fallback: set component input
    try {
      if (this.pdfViewer) {
        (this.pdfViewer as any).zoom = String(this.zoom);
        if (typeof (this.pdfViewer as any).refresh === 'function') (this.pdfViewer as any).refresh();
      }
    } catch (e) { /* ignore */ }
  }

  @HostListener('wheel', ['$event'])
  zoomInOut(event: WheelEvent) {
    event.preventDefault();

    const delta = Math.max(-1, Math.min(1, event.deltaY || -event.detail));

    // Zoom out
    if (delta > 0 && this.zoom > this.minZoom) {
      this.zoom -= this.zoomIncrement;
    }
    // Zoom in
    else if (delta < 0 && this.zoom < this.maxZoom) {
      this.zoom += this.zoomIncrement;
    }
  }

  rotate() {
    this.rotation += 90;
    this.rotation %= 360;
    // Try using the embedded PDF.js API exposed by the viewer component.
    try {
      const app = (this.pdfViewer as any)?.PDFViewerApplication;
      const pdfViewer = app?.pdfViewer;
      if (pdfViewer && typeof pdfViewer.pagesRotation === 'number') {
        pdfViewer.pagesRotation = (pdfViewer.pagesRotation + 90) % 360;
        // Notify event bus if available so UI updates.
        if (app?.eventBus && typeof app.eventBus.dispatch === 'function') {
          try { app.eventBus.dispatch('rotationchanged', { rotation: pdfViewer.pagesRotation }); } catch (e) { /* ignore */ }
        }
        return;
      }
    } catch (err) {
      console.warn('rotate: PDFViewerApplication rotate failed, falling back', err);
    }

    // Fallback: toggle the rotatecw input flag on the component so the
    // library handles rotation if the direct API isn't available.
    try {
      if (this.pdfViewer) {
        (this.pdfViewer as any).rotatecw = true;
        if (typeof (this.pdfViewer as any).refresh === 'function') {
          (this.pdfViewer as any).refresh();
        }
        setTimeout(() => { try { (this.pdfViewer as any).rotatecw = false; } catch (e) { } }, 120);
      }
    } catch (err) {
      console.warn('rotate fallback failed', err);
    }
  }

  antiRotate() {
    this.rotation -= 90;
    this.rotation %= 360;
    try {
      const app = (this.pdfViewer as any)?.PDFViewerApplication;
      const pdfViewer = app?.pdfViewer;
      if (pdfViewer && typeof pdfViewer.pagesRotation === 'number') {
        pdfViewer.pagesRotation = (pdfViewer.pagesRotation - 90 + 360) % 360;
        if (app?.eventBus && typeof app.eventBus.dispatch === 'function') {
          try { app.eventBus.dispatch('rotationchanged', { rotation: pdfViewer.pagesRotation }); } catch (e) { /* ignore */ }
        }
        return;
      }
    } catch (err) {
      console.warn('antiRotate: PDFViewerApplication rotate failed, falling back', err);
    }

    try {
      if (this.pdfViewer) {
        (this.pdfViewer as any).rotateccw = true;
        if (typeof (this.pdfViewer as any).refresh === 'function') {
          (this.pdfViewer as any).refresh();
        }
        setTimeout(() => { try { (this.pdfViewer as any).rotateccw = false; } catch (e) { } }, 120);
      }
    } catch (err) {
      console.warn('antiRotate fallback failed', err);
    }
  }

  /**
   * This method will toggle fullscreen mode
   * and based on mode it will prevent page scroll
   */
  toggleFullScreen() {
    const element = document.documentElement;

    if (this.isFullScreen && document.exitFullscreen) {
      document.exitFullscreen();
    } else if (!this.isFullScreen && element.requestFullscreen) {
      element.requestFullscreen();
    }
  }

  /**
   * This method will toggle sidebar visibility
   * if invoice is in full screen mode then sidebar will be hidden
   * and vice-versa.
   * @param canHideSidebar 
   */
  toggleSidebar(canHideSidebar: boolean) {
    const sidebarElement = document.getElementsByClassName('sidebar-open');
    if (sidebarElement) {
      if (canHideSidebar) {
        sidebarElement[0]?.classList.add('d-none');
      } else if (!canHideSidebar) {
        sidebarElement[0]?.classList.remove('d-none');
      }
    }
  }

  /**
   * Below Host listener is required to listen fullscreen mode change
   * It will be used to exit from fullscreen mode
   */
  @HostListener('document:fullscreenchange', ['$event'])
  fullScreen() {
    this.isFullScreen = !this.isFullScreen;
    document.body.style.overflow = this.isFullScreen ? '' : 'hidden';
    this.fullScreenEvent.emit({ isFullScreen: this.isFullScreen });
    if (this.isFullScreen) {
      this.toggleSidebar(true);
    } else {
      this.toggleSidebar(false);
    }
  }

  @HostListener('mousedown', ['$event'])
  startDrag(e: MouseEvent) {
    e.preventDefault();
    this.isDragging = true;
    this.startX = e.clientX;
    this.startY = e.clientY;
  }

  @HostListener('mouseup')
  endDrag() {
    this.isDragging = false;
    this.initialPosX = this.posX;
    this.initialPosY = this.posY;
  }

  @HostListener('mousemove', ['$event'])
  drag(e: MouseEvent) {
    if (this.isDragging) {
      e.preventDefault();

      this.posX = this.initialPosX + e.clientX - this.startX;
      this.posY = this.initialPosY + e.clientY - this.startY;
    }
  }

  leave() {
    this.endDrag();
  }

  reset() {
    this.isDragging = false;
    this.zoom = 1;
    this.rotation = 0;
    this.initialPosX = 0;
    this.initialPosY = 0;
    this.posX = 0;
    this.posY = 0;
  }

  /**
   * This method will emit close event to close modal
   */
  onClose() {
    this.reset();
    this.closeEvent.emit({ isCloseClicked: true });
  }

}